import { useCallback, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Dropdown, { Item } from './Dropdown';
import ToolbarDropdownAutocompleteModes from './ToolbarDropdownAutocompleteModes';
import Icon from './Icon';
import useOutsideClick from 'utils/useOutsideClick';
import { setIsOpen, setOptions } from 'redux/autocomplete';

const AutocompleteDropdown = ({ onOptionClick, onCloseClick }) => {
  /* State */
  const [closeMenu, setCloseMenu] = useState(false);
  const isDebugMode = !!localStorage.getItem('debug');

  /* Selectors */
  const isOpen = useSelector((state) => state.autocomplete.isOpen);
  const options = useSelector((state) => state.autocomplete.options);
  const highlightIndex = useSelector((state) => state.autocomplete.dropdownHighlightIndex);

  /* Helpers */
  const dispatch = useDispatch();

  const { 
    top,
    height,
    left,
  } = useSelector((state) => state.autocomplete.containerData);

  useEffect(() => {
    if (!isOpen) {
      setCloseMenu(false);
    }
  }, [isOpen]);

  /* Functions */
  const handleCloseClick = () => {
    setCloseMenu(true);
  };

  const closeDropdown = useCallback(() => {
    dispatch(setIsOpen(false));
    dispatch(setOptions([]));
  }, [dispatch]);

  const dropdownRef = useOutsideClick(closeDropdown)

  return (
    isOpen && options.length && (
      <DropdownWrapper ref={dropdownRef} top={top + height} left={left}>
        <Close onClick={handleCloseClick}>
          <Icon source='feather' name='more-horizontal' />

          {closeMenu && (
            <ToolbarDropdownAutocompleteModes
              position='top'
              align='center'
              withArrow 
            />
          )}
        </Close>
        <Arrow />
        <ContentWrapper>
          {options.map((option, index) => (
            <DropdownItem
              key={`${option.text}-${index}`}
              highlight={index === highlightIndex}
              onClick={(event) => onOptionClick(event, option.text)}
            >
              {option.text}
              {isDebugMode && (
                <small>{option.extra.scoreText}</small>
              )}
            </DropdownItem>
          ))}
        </ContentWrapper>
      </DropdownWrapper>
    )
  );
}

export default AutocompleteDropdown;

AutocompleteDropdown.propTypes = {
  onOptionClick: PropTypes.func,
  onCloseClick: PropTypes.func
};

AutocompleteDropdown.defaultProps = {
  onOptionClick: () => {},
  onCloseClick: () => {},
};

const ContentWrapper = styled.div`
  margin: -6px;
  /* overflow: hidden;
  overflow-y: auto;
  max-height: 168px; */
`;

const Arrow = styled.div`
  position: absolute;
  width: 0; 
  height: 0; 
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-bottom: 6px solid var(--lizzy-white);
  top: -6px;
  left: 32px;
  margin-left: -6px;
  z-index: 12;
`;

const DropdownWrapper = styled(Dropdown)`
  position: absolute;
  top: ${p => p.top + 6}px;
  left: ${p => p.left - 32}px;
  z-index: 12;
  filter: drop-shadow(0px -10px 27px rgba(0, 0, 0, 0.09))
          drop-shadow(0px 3.75998px 11.28px rgba(0, 0, 0, 0.0503198))
          drop-shadow(0px 2.01027px 6.0308px rgba(0, 0, 0, 0.0417275))
          drop-shadow(0px 1.12694px 3.38082px rgba(0, 0, 0, 0.035))
          drop-shadow(0px 0.598509px 1.79553px rgba(0, 0, 0, 0.0282725))
          drop-shadow(0px 0.249053px 0.747159px rgba(0, 0, 0, 0.0196802));
`;

const DropdownItem = styled(Item)`
  font-size: 14px;
  display: flex;
  justify-content: space-between;
  max-width: 380px;
  white-space: pre-wrap;

  ${p => p.highlight && css`
    background-color: var(--lizzy-black-10);
  `}

  small {
    text-align: right;
  }
`;

const Close = styled.div`
  position: absolute;
  right: -6px;
  top: -6px;
  color: var(--lizzy-black-65);
  background-color:  var(--lizzy-black-10);
  width: 16px;
  height: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 13px;
  cursor: pointer;

  &:hover {
    background-color: var(--lizzy-black-35);
  }

  & > ${Icon} {
    width: 13px;
    height: 13px;
  }
`;